import {
  getNextPaydateMonthlyRelativeDate,
  getNextPaydateMonthlySpecificDate,
  getNextPaydateTwiceMonthly,
  getNextPaydays,
  lastDayOfMonth,
  WEEK_ORDINAL,
} from './datetime';
import { IncomeDataT, PaydateScheduleTypeT } from '../types/income';
import { PaymentDayValueT } from '../types/income-details';
import {
  IncomeSourceValueT, isBenefitsIncomeSource, isEmployed,
} from '../types/income-source';

export type IncomeSourceApiValueT =
  | 'employment'
  | 'self_employment'
  | 'alimony/child_support'
  | 'retired_benefits'
  | 'social_security/disability_income'
  | 'unemployment_benefits'
  | 'other';

export type IncomeSubmitRequestT = {
  net_pay_per_check: string;
  paydate_frequency: string;
  paydate_next_paydate: string;
  payment_method: string;
  source_of_income: IncomeSourceApiValueT;
  benefits_dont_expire?: boolean;
  benefits_expiry?: string;
  employer_name?: string;
  paydate_day_of_week?: string;
  paydate_ordinal?: string;
  paydate_schedule_type?: PaydateScheduleTypeT;
  position?: string;
  work_phone?: string;
};

const INCOME_SOURCE_API_MAP: Record<IncomeSourceValueT, IncomeSourceApiValueT> = {
  'alimony/child_support': 'alimony/child_support',
  employed: 'employment',
  other: 'other',
  retired_benefits: 'retired_benefits',
  self_employed: 'self_employment',
  'social_security/disability_income': 'social_security/disability_income',
  unemployment_benefits: 'unemployment_benefits',
};

const capitalizeDay = (day: PaymentDayValueT): string => day.charAt(0).toUpperCase() + day.slice(1);

// Transforms the internal IncomeDataT store shape into the API request body
// expected by PUT /api/v1/accounts.
export const buildIncomeSubmitRequest = (income: IncomeDataT): IncomeSubmitRequestT => {
  const {
    amount,
    benefits,
    day,
    employment,
    frequency,
    method,
    monthlyPaymentDay,
    nextPayday,
    paymentDays,
    paymentSchedule,
    paymentWeek,
    source,
  } = income;

  let paydateNextPaydate = nextPayday ?? '';

  if (frequency === 'weekly' && day) {
    paydateNextPaydate = getNextPaydays(day, 1)[0].value;
  } else if (frequency === 'twice_monthly' && paymentDays) {
    paydateNextPaydate = getNextPaydateTwiceMonthly(paymentDays);
  } else if (frequency === 'monthly') {
    if (paymentSchedule === 'same_day_each_month' && monthlyPaymentDay) {
      paydateNextPaydate = getNextPaydateMonthlySpecificDate(monthlyPaymentDay);
    } else if (paymentSchedule === 'same_day_particular_week' && paymentWeek && day) {
      paydateNextPaydate = getNextPaydateMonthlyRelativeDate(paymentWeek, day);
    }
  }

  const request: IncomeSubmitRequestT = {
    net_pay_per_check: (amount ?? 0).toFixed(2),
    paydate_frequency: frequency ?? '',
    paydate_next_paydate: paydateNextPaydate,
    payment_method: method ?? '',
    source_of_income: INCOME_SOURCE_API_MAP[source as IncomeSourceValueT] ?? source as IncomeSourceApiValueT,
  };

  // Employment-specific fields
  if (isEmployed(source)) {
    request.employer_name = employment?.employer;
    request.position = employment?.title;
    request.work_phone = employment?.phoneNumber;
  }

  // Benefits-specific fields
  if (isBenefitsIncomeSource(source)) {
    const willExpire = benefits?.willExpire;

    request.benefits_dont_expire = willExpire === 'no';

    if (willExpire === 'yes' && benefits?.expiration) {
      const [year, month] = benefits.expiration.split('-').map(Number);
      const lastDay = lastDayOfMonth(year, month);

      request.benefits_expiry = `${benefits.expiration}-${String(lastDay).padStart(2, '0')}`;
    }
  }

  // Paydate frequency conditional fields
  if (frequency === 'weekly' || frequency === 'biweekly') {
    if (day) {
      request.paydate_day_of_week = capitalizeDay(day);
    }
  } else if (frequency === 'twice_monthly') {
    if (paymentDays) {
      request.paydate_ordinal = paymentDays.replace('_and_', ';').replace('last', '-1');
    }
  } else if (frequency === 'monthly') {
    if (paymentSchedule === 'same_day_each_month') {
      request.paydate_schedule_type = 'specific_date';
      request.paydate_ordinal = monthlyPaymentDay === 'last_day' ? '-1' : monthlyPaymentDay;
    } else if (paymentSchedule === 'same_day_particular_week') {
      request.paydate_schedule_type = 'relative_date';
      request.paydate_ordinal = paymentWeek ? String(WEEK_ORDINAL[paymentWeek]) : undefined;

      if (day) {
        request.paydate_day_of_week = capitalizeDay(day);
      }
    }
  }

  return request;
};
